import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Clock, User, CalendarDays } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import axios, { API } from "@/lib/api";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";
import { useSite } from "@/context/SiteContext";

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
}

function AdBanner({ image, link, side }) {
  if (!image) return null;
  const img = <img src={image} alt={`Iklan ${side}`} className="w-full rounded-2xl object-cover shadow-[0_2px_16px_rgba(15,107,79,0.08)]" />;
  return (
    <aside className="hidden lg:block" data-testid={`article-ad-banner-${side}`}>
      <div className="sticky top-24">
        {link ? <a href={link} target="_blank" rel="noopener noreferrer">{img}</a> : img}
      </div>
    </aside>
  );
}

export default function ArtikelDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { settings } = useSite();

  useEffect(() => {
    axios.get(`${API}/public/articles/${slug}`).then(({ data }) => {
      setArticle(data);
      document.title = `${data.title} - KBIHU Muhammadiyah Surabaya`;
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="py-20"><EmptyState title="Artikel tidak ditemukan" description="Artikel yang Anda cari mungkin sudah dihapus atau tidak tersedia." /></div>;
  if (!article) return <LoadingScreen />;

  return (
    <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-14 sm:px-6 lg:grid-cols-[180px_1fr_180px]" data-testid="artikel-detail-page">
      <AdBanner image={settings.ad_left_image} link={settings.ad_left_link} side="left" />
      <article className="min-w-0">
        <Badge variant="outline" className="rounded-full border-[#0F6B4F]/30 text-[#0F6B4F]">{article.category}</Badge>
        <h1 className="mt-4 font-heading text-3xl font-bold text-[#1F2937] sm:text-4xl">{article.title}</h1>
        <div className="mt-4 flex items-center gap-4 text-sm text-[#64748B]">
          <span className="flex items-center gap-1"><User className="h-4 w-4" /> {article.author}</span>
          <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" /> {formatDate(article.published_at)}</span>
          <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {article.reading_time} menit</span>
        </div>
        <img src={article.cover_image} alt={article.title} className="mt-8 aspect-video w-full rounded-2xl object-cover" />
        <div className="mt-8 whitespace-pre-line text-base leading-relaxed text-[#1F2937]">{article.content}</div>
      </article>
      <AdBanner image={settings.ad_right_image} link={settings.ad_right_link} side="right" />
    </div>
  );
}
